import { useState } from 'react';

import { AuthGuard } from '../../app/AuthGuard';
import { customerCabinetApi } from '../../features/customerCabinet/api';
import { CustomerCabinetLayout, navigateCustomer } from '../../layouts/CustomerCabinet/CustomerCabinetLayout';
import { Button, Stars, Tile, type StarsRating } from '../../ui';
import { CustomerPageHeading } from './customerCabinetShared';
import './customer-cabinet.css';

const LIKED_OPTIONS = [
  ['photo_reports', '📷 Фото-отчёты в пути'],
  ['search', '🔍 Удобный поиск'],
  ['chat', '💬 Чат с перевозчиком'],
  ['reviews', '⭐ Рейтинги и отзывы'],
  ['verification', '✓ Верификация'],
  ['free', '💸 Бесплатно для меня'],
] as const;

const NPS_OPTIONS = [
  ['yes', '👍 Точно да'],
  ['maybe', '🤔 Возможно'],
  ['no', '👎 Нет'],
] as const;

export function PlatformFeedbackScreen() {
  const [usability, setUsability] = useState(5);
  const [liked, setLiked] = useState<string[]>(['photo_reports', 'search']);
  const [improvement, setImprovement] = useState('');
  const [nps, setNps] = useState('yes');
  const [state, setState] = useState<'ready' | 'submitting' | 'error'>('ready');

  function toggleLiked(value: string) {
    setLiked((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  }

  async function submit() {
    setState('submitting');
    try {
      await customerCabinetApi.createPlatformFeedback({
        source_flow: 'customer_review',
        usability_rating: usability,
        liked_options: liked,
        improvement_text: improvement,
        nps_intent: nps,
      });
      navigateCustomer('my-requests');
    } catch {
      setState('error');
    }
  }

  return (
    <AuthGuard allow={['customer']}>
      <CustomerCabinetLayout
        contentHeight={{ desktop: 1055, tablet: 811, mobile: 811 }}
        figmaNode={{ '1920': '2736:42249', '1440': '2736:42990', '768': null, '375': null }}
        screen="platform-feedback"
      >
        <div className="customer-platform-feedback" data-state={state} data-figma-breakpoints="1920,1440">
          <div className="customer-platform-feedback__source">
            <CustomerPageHeading
              className="customer-platform-feedback__title"
              figmaNode="2736:43362"
              title="Поможете нам стать лучше?"
              subtitle="Спасибо за отзыв! Теперь пара секунд про сам сервис — что понравилось, что бесит. Мы реально читаем каждый ответ и используем для улучшений."
            />

            <section className="customer-platform-feedback__form" data-figma-node="2736:43366">
              <div className="customer-platform-feedback__rating">
                <strong>Насколько удобно вам было пользоваться LOVO?</strong>
                <div>
                  <Stars mode="Light" rating={usability as StarsRating} />
                  <span className="customer-platform-feedback__rating-hitareas">{[1, 2, 3, 4, 5].map((value) => <button aria-label={`${value} из 5`} aria-pressed={usability === value} key={value} type="button" onClick={() => setUsability(value)} />)}</span>
                </div>
              </div>

              <fieldset className="customer-platform-feedback__liked">
                <legend>Что особенно понравилось?</legend>
                <div>{LIKED_OPTIONS.map(([value, title]) => <Tile active={liked.includes(value)} className="customer-platform-feedback__tile" key={value} size="small" title={title} onClick={() => toggleLiked(value)} />)}</div>
              </fieldset>

              <label className="customer-platform-feedback__comment">
                <span>Что можно улучшить?</span>
                <textarea placeholder="Например: добавить GPS-трекинг, увеличить количество перевозчиков в моём регионе, упростить выбор..." value={improvement} onChange={(event) => setImprovement(event.target.value)} />
              </label>

              <fieldset className="customer-platform-feedback__nps">
                <legend>Готовы рекомендовать LOVO друзьям и знакомым?</legend>
                <div>{NPS_OPTIONS.map(([value, title]) => <Tile active={nps === value} className="customer-platform-feedback__tile" key={value} size="small" title={title} onClick={() => setNps(value)} />)}</div>
              </fieldset>

              <div className="customer-platform-feedback__actions">
                <div><Button disabled={state === 'submitting'} kind="primary" size="big" onClick={() => void submit()}>Отправить отзыв о платформе</Button><Button kind="secondary" size="big" onClick={() => navigateCustomer('my-requests')}>Пропустить</Button></div>
                <p>Ваши отзывы помогают нам становиться лучше · команда LOVO лично читает каждый</p>
              </div>
            </section>
          </div>
        </div>
      </CustomerCabinetLayout>
    </AuthGuard>
  );
}
